import { useState } from "react";
import { Heart, Plus, Settings2, Users } from "lucide-react";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ConfiguratorSheet from "@/components/ConfiguratorSheet";

const PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='100%25' height='100%25' fill='%23e5e5e5'/></svg>";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { user, toggleFavorite } = useAuth();
  const [configOpen, setConfigOpen] = useState(false);

  const hasConfig = product.configurable || (product.option_group_ids && product.option_group_ids.length > 0);
  const isFav = user?.favorites?.includes(product.id);
  const soldOut = product.sold_out;

  const handleAdd = () => {
    if (hasConfig) { setConfigOpen(true); return; }
    addItem({
      product_id: product.id, nom: product.nom, prix: product.prix,
      image: product.image, servings: product.servings, configurable: false, quantity: 1,
    });
  };

  return (
    <>
      <div
        data-testid={`product-card-${product.id}`}
        className="group relative flex flex-col rounded-2xl overflow-hidden bg-ink-800 border border-border hover:border-coral/40 transition-colors animate-fade-up"
        style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
      >
        <div className="dark relative aspect-[4/3] overflow-hidden bg-ink-700">
          <img
            src={product.image || PLACEHOLDER}
            alt={product.nom}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-transparent" />

          <button
            data-testid={`product-fav-${product.id}`}
            onClick={() => toggleFavorite(product.id)}
            className="absolute top-3 right-3 h-9 w-9 rounded-full glass border border-border flex items-center justify-center hover:scale-110 transition-transform"
            aria-label="Favori"
          >
            <Heart className={`h-4 w-4 ${isFav ? "fill-coral text-coral" : "text-foreground"}`} />
          </button>

          {product.servings > 1 && (
            <span className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full glass border border-border text-xs font-medium text-foreground">
              <Users className="h-3.5 w-3.5 text-coral" /> Pour {product.servings} personnes
            </span>
          )}
          {soldOut && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-red-500/90 text-foreground text-xs font-semibold">Épuisé</span>
          )}
        </div>

        <div className="flex flex-col flex-1 p-4">
          <h3 className="font-heading font-semibold text-foreground leading-snug">{product.nom}</h3>
          {product.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2 flex-1">{product.description}</p>
          )}
          <div className="flex items-center justify-between mt-4">
            <span className="font-heading text-lg font-bold text-coral">{money(product.prix)}</span>
            <button
              data-testid={`product-add-${product.id}`}
              onClick={handleAdd}
              disabled={soldOut}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground font-heading text-sm font-medium transition-colors active:scale-95"
            >
              {hasConfig ? (<><Settings2 className="h-4 w-4" /> Configurer</>) : (<><Plus className="h-4 w-4" /> Ajouter</>)}
            </button>
          </div>
        </div>
      </div>

      {hasConfig && (
        <ConfiguratorSheet product={product} open={configOpen} onClose={() => setConfigOpen(false)} />
      )}
    </>
  );
}
